import getGlobalConfig from '../globalConfig';

const { minFullTextSearchTermLength } = getGlobalConfig();

/**
 * A search answered without the server, and why. The three differ in what the user can do about it,
 * which is the whole reason they are told apart.
 *
 * `termTooShort` — the term is below `minFullTextSearchTermLength`, so `fullTextSearch.preFilter`
 * never asked the server. Typing another character fixes it.
 *
 * `serverUnavailable` — it asked and could not be answered (offline, or a search index the
 * environment does not have), so `preFilter` dropped back to the local search. Nothing the user
 * types helps, but it is a fault rather than a setting, and may right itself.
 *
 * `noServerSearch` — the relation has no server search at all, so no term of any length is ever
 * sent. Permanent for that relation until someone enables full text search on it. This is the one
 * that actually occurs today: `location:location` is size limited with `supportsFullTextSearch`
 * false, which is 30 of RB-RFID's dropdown controls (#2047).
 */
export const LOCAL_ONLY_SEARCH_REASONS = {
    termTooShort: 'termTooShort',
    serverUnavailable: 'serverUnavailable',
    noServerSearch: 'noServerSearch'
};

/**
 * Says whether the rows a view is currently showing are the whole answer to the user's search, or
 * only the part of it that happens to be on this device.
 *
 * The grid prints "End of Records" under its last row. On a `limitSyncSize` relation searched
 * locally that is a false claim — the local copy is a bounded subset of the tenant, so a search
 * over it finds a bounded subset of the matches. #2040 is exactly that: 47 items start "KP", a two
 * character search found the 7 that were synced, and the grid declared the list complete. The
 * caller uses this reason to say what the list actually is instead.
 *
 * Returns undefined whenever "End of Records" is honest:
 *  - no search term — nothing was searched, so nothing was narrowed;
 *  - the relation is not size limited — the device holds the whole tenant, so a local search over
 *    it is complete, whether or not a server search exists;
 *  - the server did answer — `serverResults` holds ids, which is what `getMql` filtered on.
 *
 * Size limited is the load-bearing condition, not full text search. A relation with no server
 * search is not thereby complete; it is incomplete with no way to ask for the rest, which is worth
 * saying rather than hiding (#2047).
 *
 * @param {object} filters - as returned by `viewCriteria.getFiltersFromView()`
 * @param {object} [relationMetadata] - the view's relation metadata
 * @returns {string|undefined} a value from LOCAL_ONLY_SEARCH_REASONS, or undefined
 */
export default function getLocalOnlySearchReason(filters, relationMetadata) {
    const searchTerm = filters?.fullTextSearch?.searchTerm;
    if (!searchTerm) return undefined;
    if (!relationMetadata?.limitSyncSize) return undefined;
    if (!relationMetadata?.supportsFullTextSearch) return LOCAL_ONLY_SEARCH_REASONS.noServerSearch;
    if (Object.keys(filters.fullTextSearch.serverResults ?? {}).length > 0) return undefined;

    return searchTerm.length < minFullTextSearchTermLength
        ? LOCAL_ONLY_SEARCH_REASONS.termTooShort
        : LOCAL_ONLY_SEARCH_REASONS.serverUnavailable;
}
